import VueRouter, { Route } from 'vue-router';
import { DateTime, LocaleOptions, DateTimeFormatOptions } from 'luxon';

import { ElmerError, ElmerErrorsByModuleName } from 'frontend/types/elmer';
import {
  DEFAULT_EDITOR_VIEW,
  EditorView,
  ElmCompiledAsset,
  isJSONConfigAsset,
  LessonAsset,
  LessonSourceObject,
  ParsedEditorHashPath,
} from 'frontend/types/lessons/editor';
import { Optional } from 'frontend/types';
import { headerLineCount } from 'frontend/views/lessons/editor/compilationUtils';

// Suffix appended to previews to allow for showing both the narrow and wide
// previews simultaneously. This should NOT collide with the suffix used when
// duplicating an asset.
export const PREVIEW_SUFFIX = '--extra--for--wide--preview';
// Suffix appended to the asset alias when duplicating an asset.
const DUPLICATE_SUFFIX = '-copy';

export function lessonBaseUrlParams(route: Route) {
  return {
    courseSlug: route.params.courseSlug,
    chapterSlug: route.params.chapterSlug,
    lessonSlug: route.params.lessonSlug,
  };
}

export function previewUrl(router: VueRouter, versionName: string): string {
  return router.resolve({
    name: 'lesson',
    params: {
      ...lessonBaseUrlParams(router.currentRoute),
    },
    query: {
      version_name: versionName,
    },
  }).href;
}

export function externalPreviewUrl(router: VueRouter, previewToken: string): string {
  return router.resolve({
    name: 'lesson',
    params: {
      ...lessonBaseUrlParams(router.currentRoute),
    },
    query: {
      preview_token: previewToken,
    },
  }).href;
}

function timeFormat(): LocaleOptions & DateTimeFormatOptions {
  return {
    ...DateTime.DATETIME_MED,
    timeZoneName: 'short',
  };
}

export function formatTs(ts: string): string {
  return DateTime.fromISO(ts).toLocaleString(timeFormat());
}

export function elmerErrorToErrorDict(
  elmerReport: ElmerError,
  asset?: ElmCompiledAsset,
): ElmerErrorsByModuleName {
  const result: ElmerErrorsByModuleName = {};
  switch (elmerReport.type) {
    case 'compile-errors':
      // This is a list of errors, organized by file.
      // Each element has all of the errors for a single module (== file).
      elmerReport.errors.forEach(problemsInModule => {
        const [moduleName] = problemsInModule.name.split('.');

        // figure out the module-wide offset
        let lineNumberOffset = 0;
        if (problemsInModule.name === 'Main') {
          if (!asset) {
            throw new Error(
              `Received the following elm compile error: ${problemsInModule}
It appears that a Main.elm file was compiled outside the context of writing an Asset.`,
            );
          }

          lineNumberOffset = headerLineCount(asset);
        }

        // change the line numbers for every problem...
        result[moduleName] = problemsInModule.problems.map(p => {
          // ... in the metadata
          p.region.start.line -= lineNumberOffset;
          p.region.end.line -= lineNumberOffset;

          // ... and in the message itself
          p.message = p.message.map(m => {
            if (typeof m === 'string') {
              return adjustElmLineNumbersInString(m, lineNumberOffset);
            }
            m.string = adjustElmLineNumbersInString(m.string, lineNumberOffset);
            return m;
          });

          return p;
        });
      });
      break;

    case 'error':
      // this case only happens if the elm compiler really chokes (typically a syntax error)
      result.Main = elmerReport.message;
      break;

    default:
      result.Main = [`${elmerReport}`];
  }
  return result;
}

export function extractAssetsFromLessonSource(lessonSource: LessonSourceObject): LessonAsset[] {
  return lessonSource.content.assets.map((asset: LessonAsset) => {
    if (isJSONConfigAsset(asset)) {
      return {
        ...asset,
        json: JSON.stringify(asset.json),
      };
    }
    return asset;
  });
}

interface ElmCompilerMessage {
  string: string;
}

type ErrorMessage = string | ElmCompilerMessage;

// Note that line number adjustment for compilation errors is done in the backend code, unlike for format.
// See brilliant/visualizations/views.py : DiagrammarCompilationView
export function elmCompilerMessageToString(errorMessage: ErrorMessage[]): string {
  return errorMessage
    .map(frag => {
      if (typeof frag !== 'string') {
        return frag.string;
      }
      return frag;
    })
    .join('');
}

// fix line numbers in Elm error messages
export function adjustElmLineNumbersInString(
  errorMessage: string,
  lineNumberOffset: number = 0,
): string {
  return errorMessage.replace(/^(\d+)\|/gm, (lineNumber: string) => {
    const curLine = parseInt(lineNumber, 10);
    const newLine = curLine - lineNumberOffset;

    const leftPad = curLine.toString().length - newLine.toString().length;
    const spaces = ' '.repeat(leftPad);

    return `${spaces}${newLine.toString()}|`;
  });
}

export function elmCompilerProblemToAnnotation(problem: any) {
  return {
    row: problem.region.start.line,
    column: problem.region.start.column,
    type: 'error',
    text: elmCompilerMessageToString(problem.message),
  };
}

function segmentToLineNumbers(segment: string | undefined): undefined | [number, number] {
  if (segment === undefined) {
    return undefined;
  }

  if (segment.includes('-')) {
    const [start, end] = segment.split('-').map(str => parseInt(str, 10));
    if (segment === `${start}-${end}`) {
      return [start, end];
    }
    return undefined;
  }

  const lineNumber = parseInt(segment, 10);
  if (segment === `${lineNumber}`) {
    return [lineNumber, lineNumber];
  }
  return undefined;
}

export function getParsedEditorHashPath(route: Route): ParsedEditorHashPath {
  const defaultResult: ParsedEditorHashPath = { view: DEFAULT_EDITOR_VIEW };
  const [_, maybeView, maybeSubView, maybeLineNumbers] = route.hash.split('/');
  if (maybeView) {
    switch (maybeView as EditorView) {
      case 'lesson':
      case 'compiledElmMain':
        return {
          view: maybeView as EditorView,
          highlightLines: segmentToLineNumbers(maybeSubView),
        };
      case 'assets':
      case 'settings':
        return {
          view: maybeView as EditorView,
          subview:
            maybeSubView && maybeSubView.trim().length
              ? decodeURIComponent(maybeSubView)
              : undefined,
          highlightLines: segmentToLineNumbers(maybeLineNumbers),
        };
      default:
        return defaultResult;
    }
  }
  return defaultResult;
}

function highlightLinesHashPath(highlightLines: undefined | [number, number]): string {
  if (!highlightLines) {
    return '';
  }

  if (highlightLines[0] >= highlightLines[1]) {
    return `${highlightLines[0]}/`;
  }

  return `${highlightLines[0]}-${highlightLines[1]}/`;
}

function buildEditorHashPathString({
  view,
  subview,
  highlightLines,
}: ParsedEditorHashPath): Optional<string> {
  if (view === 'lesson') {
    return highlightLines ? `#/${view}/${highlightLinesHashPath(highlightLines)}` : undefined;
  }
  if (view === 'compiledElmMain') {
    return `#/${view}/${highlightLinesHashPath(highlightLines)}`;
  }
  return subview
    ? `#/${view}/${encodeURIComponent(subview)}/${highlightLinesHashPath(highlightLines)}`
    : `#/${view}/`;
}

export function setEditorHashPath(
  router: VueRouter,
  currentRoute: Route,
  hashPath: ParsedEditorHashPath,
  replace: boolean = false,
) {
  const { view, subview, highlightLines } = getParsedEditorHashPath(currentRoute);
  if (
    view !== hashPath.view ||
    subview !== hashPath.subview ||
    JSON.stringify(highlightLines) !== JSON.stringify(hashPath.highlightLines)
  ) {
    const nextLocation = {
      ...currentRoute,
      hash: buildEditorHashPathString(hashPath),
    };
    if (replace) {
      router.replace(nextLocation);
    } else {
      router.push(nextLocation);
    }
  }
}

// Will move all lines of the current selection (if any)
// If no selection will just move the current line
export function moveCurrentSelection(cm: any, distance: number) {
  const { line, ch } = cm.getCursor();
  const { line: anchorLine, ch: anchorCh } = cm.getCursor('anchor');
  const firstLine = cm.getCursor('from').line;
  const lastLine = cm.getCursor('to').line;
  if ((firstLine > 0 && distance < 0) || (lastLine < cm.lastLine() && distance > 0)) {
    const selectionLines = [];
    for (let i = firstLine; i <= lastLine; i += 1) {
      selectionLines.push(cm.getLine(i));
    }
    const selectionText = `${selectionLines.join('\n')}\n`;
    cm.replaceRange('', { line: firstLine, ch: 0 }, { line: lastLine + 1, ch: 0 });
    cm.replaceRange(selectionText, { line: firstLine + distance, ch: 0 });
    cm.setCursor({ ch, line: line + distance });
    if (anchorLine > line) {
      cm.setSelection({ line: lastLine + distance, ch: anchorCh }, { ch, line: line + distance });
    } else {
      cm.setSelection(
        { line: firstLine + distance, ch: anchorCh },
        { ch, line: lastLine + distance },
      );
    }
  }
}

export function useMacShortcuts(): boolean {
  if (!navigator) {
    return false;
  }
  return /Apple Computer/.test(navigator.vendor) || /Mac/.test(navigator.platform);
}

export interface KeyboardShortcut {
  wasPressed: (event: KeyboardEvent) => boolean;
  string: string;
}

export function hasPlatformCModifierKey(e: KeyboardEvent): boolean {
  return useMacShortcuts() ? e.metaKey : e.ctrlKey;
}

export function cModifierString(): string {
  if (process.env.VUE_ENV === 'server') {
    return 'Ctrl/Cmd';
  }
  return useMacShortcuts() ? 'Cmd' : 'Ctrl';
}

export const previewShortcut = {
  wasPressed: (event: KeyboardEvent) => hasPlatformCModifierKey(event) && event.key === 'p',
  string: `${cModifierString()} + P`,
};

export const formatShortcut = {
  wasPressed: (event: KeyboardEvent) => hasPlatformCModifierKey(event) && event.key === 'i',
  string: `${cModifierString()} + I`,
};

export const saveShortcut = {
  wasPressed: (event: KeyboardEvent) => hasPlatformCModifierKey(event) && event.key === 's',
  string: `${cModifierString()} + S`,
};

export function assetFieldId(alias: string, fieldName: string): string {
  return `${alias}__${fieldName}`;
}

/**
 * Ensure that an alias is unique by appending `-N` to the alias. This function
 * _always_ applies the `-N` suffix.
 */
export function indexedUniqueAssetAlias(
  key: string,
  index: number,
  lessonAssets: LessonAsset[],
  assetIndex?: number,
): string {
  const alias = `${key}-${index}`;
  if (lessonAssets.filter((l, i) => l.alias === alias && i !== assetIndex).length !== 0) {
    return indexedUniqueAssetAlias(key, index + 1, lessonAssets);
  }
  return alias;
}

const ALIAS_COPY_REGEX = new RegExp(`(?<baseAlias>.*)${DUPLICATE_SUFFIX}(?<count>[0-9]*)$`);
type AliasCopyGroups = { baseAlias: string; count: string | undefined } & { [key: string]: string };

/**
 * Rules for unique alias naming:
 * 1. If an asset’s alias does not have the postfix -copy([0-9]*), then
 *    duplicating it leads to a new asset with the alias ${old_alias}-copy.
 * 2. If an asset’s alias does have the postfix -copy([0-9]*), then duplicating it
 *    leads to a new asset with the alias ${old_alias_prefix}-copy{n}, where n
 *    is the smallest strictly positive integer greater than k for any asset
 *    matching the structure ${old_alias_prefix}-copy{k}.
 *    For example:
 *    - If file-copy1, file-copy6, and file-copy19 exist,
 *    - then you’ll get file-copy20.
 *
 * Note: The regex for the postfix -copy([0-9]*) is written to treat leading
 *       zeroes as the same as no leading zeroes.
 *       For example:
 *       - If file-copy1, file-copy6, file-copy001, and file-copy019 exist,
 *       - then you’ll get file-copy20.
 *
 * @param {string} alias - The asset alias we are attempting to create.
 * @param {LessonAsset[]} lessonAssets - The list of existing assets.
 * @returns {string} Guaranteed unique alias that can be inserted into the list
 *                   of assets. If the given `alias` is already unique, that it
 *                   will be returned. Otherwise, a new unique one will be
 *                   generated using the described ruleset.
 */
export function toUniqueAssetAlias(alias: string, lessonAssets: LessonAsset[]): string {
  if (lessonAssets.filter(l => l.alias === alias).length !== 0) {
    const baseAlias = alias.match(ALIAS_COPY_REGEX)?.groups?.baseAlias ?? alias;
    const aliasRegex = new RegExp(`(?<baseAlias>${baseAlias})${DUPLICATE_SUFFIX}(?<count>[0-9]*)$`);

    const aliases = lessonAssets
      .map(l => l.alias.match(aliasRegex)?.groups)
      .filter<AliasCopyGroups>((groups): groups is AliasCopyGroups =>
        Boolean(
          groups &&
            typeof groups.baseAlias === 'string' &&
            (typeof groups.count === 'string' || groups.count === undefined),
        ),
      )
      .map(groups => ({
        baseAlias: groups.baseAlias,
        count: groups.count ? parseInt(groups.count, 10) : 0,
      }))
      .sort((a, b) => b.count - a.count);

    if (aliases.length > 0) {
      return `${aliases[0].baseAlias}${DUPLICATE_SUFFIX}${aliases[0].count + 1}`;
    }

    return `${alias}${DUPLICATE_SUFFIX}`;
  }
  return alias;
}

/**
 * Ensure that an alias is unique by appending `-N` to the alias or incrementing
 * it if it's already a suffix. Returns the original if it's already unique.
 */
export function uniqueAssetAlias(
  alias: string,
  lessonAssets: LessonAsset[],
  assetIndex?: number,
): string {
  if (lessonAssets.filter((l, i) => l.alias === alias && i !== assetIndex).length !== 0) {
    const match = alias.match(/(.*)-(\d+)/);
    if (match) {
      return indexedUniqueAssetAlias(
        match[1],
        parseInt(match[2], 10) + 1,
        lessonAssets,
        assetIndex,
      );
    }
    return indexedUniqueAssetAlias(alias, 1, lessonAssets, assetIndex);
  }
  return alias;
}
